import Highcharts from 'highcharts/esm/highcharts.js';

export interface CustomLegendOptions extends Highcharts.LegendOptions {
    useScrollbars?: boolean;
}

// Highcharts v13 removed the internal mechanism that automatically wrapped an SVG group containing
// `useHTML` content in a synced HTML <div> (formerly exposed as `legend.group.div`). HTML legend
// items now render as native SVG `<foreignObject>` children instead. We can no longer rely on
// Highcharts to give us that wrapper div, so the plugin creates and positions its own.
type CustomLegend = Highcharts.Legend & {
    legendWrapper?: Highcharts.SVGElement;
    scrollWrapperDiv?: HTMLElement;
};

// This is all based on this example from highcharts
// https://api.highcharts.com/highcharts/legend.navigation
//
// It's reliant of properties not defined on types. We must ensure anytime the Highcharts package is updated
// that the legends with scrollbars are tested to ensure nothing has broken.
//
// The logic is controlled by the custom property 'useScrollbars' on the legend.
// I tried doing an early return if it was false or if scrollbars were not needed,
// but that was causing render issues with the legend. I think it's currently safer to
// rely on the prop, as it limits the impacted charts and scope of testing to just certain charts.
//
// Originally an event was added to the Highcharts Chart class to handle the load, and then if
// applicable, it would add the 'afterRender' event to the chart legend instance. That way only
// charts that needed a scrollable legend would have the 'afterRender' event. However, a rare intermittent
// issue was discovered where the 'afterRender' event didn't fire on initial load, resulting in the legend
// not having a scrollbar. Toggling the legend on and off fixed it. This meant the event was registered,
// it just didn't fire in time. The only guess I can make is either Highcharts didn't fire it, in which case
// there's nothing to be done, or the event was not registered in time.
// Lifting the event registration from per instance and to the class seems to have resolved it.
// This means every chart has the event, but it should only ever do something if `useScrollbars` is true
export const OPRALegendScrollPlugin = (H: typeof Highcharts) => {
    const { defined } = H;

    // Making it an arrow function actually breaks functionality
    // TODO: review for ESLint 10 - eslint-disable-next-line func-names
    H.addEvent(H.Legend, 'afterRender', function () {
        const legend = this as CustomLegend;
        const { useScrollbars, useHTML } = legend.options as CustomLegendOptions;

        if (defined(useScrollbars) && useScrollbars && useHTML) {
            if (!legend.legendWrapper) {
                // Create additional SVG element to put inside additional div
                // after first render
                legend.legendWrapper = legend.chart.renderer.createElement('svg').attr({
                    version: '1.1',
                    class: 'highcharts-scrollable-legend',
                    height: legend.legendHeight,
                    width: legend.legendWidth,
                });
            }

            if (!legend.scrollWrapperDiv) {
                // Highcharts no longer supplies a synced HTML div for this group, so we own one.
                // It's appended directly to the chart container so it can sit alongside the SVG.
                legend.scrollWrapperDiv = H.createElement(
                    'div',
                    { className: 'highcharts-scrollable-legend-wrapper' },
                    { position: 'absolute', overflow: 'auto' },
                    legend.chart.container,
                );
                legend.scrollWrapperDiv.appendChild(legend.legendWrapper.element);
            }

            // Move legend group to the new SVG element
            legend.group.add(legend.legendWrapper);

            const chartWidth = legend.chart.getContainerBox().width;
            const maxWidth = chartWidth > legend.legendWidth ? legend.legendWidth : chartWidth;
            const applyScroll = chartWidth < legend.legendWidth;
            legend.scrollWrapperDiv.style.maxWidth = `${maxWidth}px`;
            legend.align();

            // align() computes the group's final offset. Capture it before stripping the
            // transform below - the wrapper div now has to reproduce that offset itself via CSS,
            // since Highcharts no longer keeps a synced HTML div positioned for us.
            const { translateX = 0, translateY = 0 } = legend.group;
            legend.scrollWrapperDiv.style.left = applyScroll ? '0px' : `${translateX}px`;
            legend.scrollWrapperDiv.style.top = `${translateY}px`;
            legend.group.element.removeAttribute('transform');
        }
    });

    // We have to tidy up the custom legend, otherwise it can leave empty scrollable containers behind
    // It does also look like there  might be a bug in Highcharts when useHTML = true for legends. As it always
    // seems to leave behind divs, resulting in multiple legend div containers the more the legend is toggled
    // TODO: review for ESLint 10 - eslint-disable-next-line func-names
    H.wrap(H.Legend.prototype, 'destroy', function (proceed, ...args) {
        const legend = this as CustomLegend;
        proceed.apply(legend, Array.prototype.slice.call(args, 1));

        if (legend.chart) {
            const { useScrollbars } = legend.options as CustomLegendOptions;
            if (defined(useScrollbars) && useScrollbars) {
                legend.legendWrapper?.element?.remove();
                legend.scrollWrapperDiv?.remove();
                // legend.update() (e.g. toggling the legend off/on) calls this destroy() but keeps
                // reusing the same Legend instance - only legend.group gets recreated next render.
                // If we don't clear these too, afterRender's `if (!legend.legendWrapper)` guard sees
                // stale references and skips recreating them, leaving the new legend.group nested
                // inside DOM nodes that were just removed from the document above.
                legend.legendWrapper = undefined;
                legend.scrollWrapperDiv = undefined;
            }
        }
    });

    return H;
};
